import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { dashboardService } from '../services/dashboardService';
import { hasAnyRole } from '../utils/permissions';
import {
  BarChart3,
  CheckCircle,
  Building,
  User,
  TrendingUp,
  Shield,
  Settings2,
  X,
  Eye,
  EyeOff
} from 'lucide-react';

// Import dashboard widgets
import OverdueTasksWidget from '../components/dashboard/OverdueTasksWidget';
import CourtDeadlinesWidget from '../components/dashboard/CourtDeadlinesWidget';
import CaseProgressWidget from '../components/dashboard/CaseProgressWidget';
import WorkloadWidget from '../components/dashboard/WorkloadWidget';
import TodaysScheduleWidget from '../components/dashboard/TodaysScheduleWidget';
import ProjectionsWidget from '../components/dashboard/ProjectionsWidget';
import ActivityFeed from '../components/ActivityFeed';

// Goals widget
import GoalsWidget from '../components/goals/GoalsWidget';

// Activity Feed and Payment receipts
import RecentPaymentsWidget from '../components/dashboard/RecentPaymentsWidget';

// Trust dashboard widgets
import TrustSummaryWidget from '../components/dashboard/TrustSummaryWidget';
import TrustTrendsWidget from '../components/dashboard/TrustTrendsWidget';
import AccountsAttentionWidget from '../components/dashboard/AccountsAttentionWidget';
import RecentTrustActivityWidget from '../components/dashboard/RecentTrustActivityWidget';
import CaseAllocationWidget from '../components/dashboard/CaseAllocationWidget';

// Billing analytics widgets
import ARAgingWidget from '../components/dashboard/ARAgingWidget';
import RevenueWidget from '../components/dashboard/RevenueWidget';
import UnbilledWorkWidget from '../components/dashboard/UnbilledWorkWidget';
import CaseVolumeWidget from '../components/dashboard/CaseVolumeWidget';

// Widget registry — defines all toggleable widgets with metadata
const WIDGET_REGISTRY = {
  courtDeadlines:   { label: 'Court Deadlines',        category: 'Tasks & Cases', defaultVisible: true },
  todaysSchedule:   { label: "Today's Schedule",       category: 'Tasks & Cases', defaultVisible: true },
  overdueTasks:     { label: 'Overdue Tasks',          category: 'Tasks & Cases', defaultVisible: true },
  caseProgress:     { label: 'Case Progress',          category: 'Tasks & Cases', defaultVisible: true },
  workload:         { label: 'Workload',               category: 'Tasks & Cases', defaultVisible: true },
  goals:            { label: 'Goals',                  category: 'Tasks & Cases', defaultVisible: true },
  activityFeed:     { label: 'Activity Feed',          category: 'Tasks & Cases', defaultVisible: true },
  revenue:          { label: 'Revenue Summary',        category: 'Billing',       defaultVisible: true },
  arAging:          { label: 'AR Aging',               category: 'Billing',       defaultVisible: true },
  unbilledWork:     { label: 'Unbilled Work',          category: 'Billing',       defaultVisible: true },
  caseVolume:       { label: 'Case Volume by Attorney', category: 'Firm',          defaultVisible: true },
};

const getDefaultVisibility = () => {
  const defaults = {};
  for (const [key, widget] of Object.entries(WIDGET_REGISTRY)) {
    defaults[key] = widget.defaultVisible;
  }
  return defaults;
};

const Dashboard = () => {
  const { user } = useAuth();
  // FirmView toggle is gated by the Partner or Super Admin role (firm-wide metrics access)
  const canSeeFirmView = user?.roles?.includes('Partner') || user?.roles?.includes('Super Admin');
  const [activeView, setActiveView] = useState('my');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [showCustomize, setShowCustomize] = useState(false);
  const [widgetVisibility, setWidgetVisibility] = useState(getDefaultVisibility);

  // Load dashboard data and widget preferences
  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const [metricsRes, prefsRes] = await Promise.all([
        dashboardService.getMetrics(activeView),
        dashboardService.getWidgetPreferences()
      ]);
      setMetrics(metricsRes.data);

      // Load saved preferences, merge with defaults for new widgets
      const savedPrefs = prefsRes?.data?.preference_data?.widget_visibility;
      if (savedPrefs) {
        setWidgetVisibility(prev => ({ ...prev, ...savedPrefs }));
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setMetrics(null);
    } finally {
      setLoading(false);
    }
  }, [activeView]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const toggleWidget = useCallback(async (widgetKey) => {
    setWidgetVisibility(prev => {
      const updated = { ...prev, [widgetKey]: !prev[widgetKey] };
      // Save in background — don't block UI
      dashboardService.saveWidgetPreferences({ widget_visibility: updated }).catch(err =>
        console.error('Error saving widget preferences:', err)
      );
      return updated;
    });
  }, []);

  const isVisible = (key) => widgetVisibility[key] !== false;

  const allWidgetsHidden = Object.keys(WIDGET_REGISTRY).every(key => widgetVisibility[key] === false);

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header with Toggle */}
      <div className="flex items-center justify-between bg-gradient-to-r from-white to-gray-50 p-6 rounded-2xl shadow-md border border-gray-100">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary-900 via-primary-700 to-primary-500 bg-clip-text text-transparent flex items-center">
            <BarChart3 className="mr-3 h-10 w-10 text-yellow-600" />
            Dashboard
          </h1>
          <p className="text-gray-600 mt-2 font-medium">
            {activeView === 'my'
              ? `Welcome back, ${user.first_name}! Here's your current workload.`
              : 'Firm-wide overview and analytics'
            }
          </p>
        </div>

        <div className="flex items-center space-x-3">
          {/* Customize Button */}
          <button
            onClick={() => setShowCustomize(true)}
            className="px-4 py-2.5 rounded-lg text-sm font-semibold text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200 flex items-center space-x-2 border border-gray-200"
          >
            <Settings2 className="h-4 w-4" />
            <span>Customize</span>
          </button>

          {/* View Toggle — only visible to firm owners */}
          {canSeeFirmView && (
            <div className="bg-gradient-to-r from-gray-100 to-gray-200 p-1.5 rounded-xl inline-flex shadow-inner">
              <button
                onClick={() => setActiveView('my')}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 flex items-center space-x-2 ${
                  activeView === 'my'
                    ? 'bg-white text-yellow-600 shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                <User className="h-4 w-4" />
                <span>My View</span>
              </button>
              <button
                onClick={() => setActiveView('firm')}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 flex items-center space-x-2 ${
                  activeView === 'firm'
                    ? 'bg-white text-yellow-600 shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                <Building className="h-4 w-4" />
                <span>Firm View</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Customize Panel (Slide-over) */}
      {showCustomize && (
        <CustomizePanel
          widgetVisibility={widgetVisibility}
          onToggle={toggleWidget}
          onClose={() => setShowCustomize(false)}
        />
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-500"></div>
        </div>
      ) : allWidgetsHidden ? (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <EyeOff className="h-12 w-12 text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">All widgets are hidden</h3>
          <p className="text-sm text-gray-500 mb-4">Use the Customize panel to choose which widgets to display.</p>
          <button
            onClick={() => setShowCustomize(true)}
            className="inline-flex items-center px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors text-sm font-medium"
          >
            <Settings2 className="h-4 w-4 mr-2" />
            Customize Dashboard
          </button>
        </div>
      ) : activeView === 'my' ? (
        <>
          {/* MyView: Three Pinned Metric Boxes */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Box 1: Today's Schedule + Overdue Tasks */}
            {(isVisible('todaysSchedule') || isVisible('overdueTasks')) && (
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden flex flex-col">
                {isVisible('todaysSchedule') && (
                  <div className="flex-1 overflow-auto">
                    <TodaysScheduleWidget userId={user.id} />
                  </div>
                )}
                {isVisible('overdueTasks') && (
                  <div className={`${isVisible('todaysSchedule') ? 'border-t border-gray-200' : ''} flex-1 overflow-auto`}>
                    <OverdueTasksWidget viewType="my" userId={user.id} />
                  </div>
                )}
              </div>
            )}

            {/* Box 2: Most Important Cases */}
            {isVisible('caseProgress') && (
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                <CaseProgressWidget viewType="my" userId={user.id} />
              </div>
            )}

            {/* Box 3: Projections */}
            <div className="overflow-hidden">
              <ProjectionsWidget viewType="my" userId={user.id} />
            </div>
          </div>

          {/* Below Metric Boxes: Court Deadlines, Case Count, Receipts & Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Court Deadlines */}
            {isVisible('courtDeadlines') && (
              <CourtDeadlinesWidget viewType="my" userId={user.id} />
            )}

            {/* Active Case Count */}
            <div className="bg-white rounded-lg shadow border p-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
                <CheckCircle className="h-5 w-5 text-yellow-600 mr-2" />
                Active Cases
              </h3>
              <div className="flex items-center justify-center py-6">
                <div className="text-center">
                  <p className="text-5xl font-bold text-gray-900">
                    {metrics?.active_cases ?? '—'}
                  </p>
                  <p className="text-sm text-gray-500 mt-2">cases currently active</p>
                </div>
              </div>
            </div>

            {/* Recent Payment Receipts */}
            <RecentPaymentsWidget />
          </div>

          {/* Goals */}
          {isVisible('goals') && <GoalsWidget viewType="my" />}

          {/* Activity Log */}
          {isVisible('activityFeed') && <ActivityFeed limit={10} showHeader={true} />}
        </>
      ) : (
        <>
          {/* Firm View */}
          {/* Trust Overview Section - Admin+ Only */}
          {hasAnyRole(user, ['Super Admin', 'Admin']) && (
            <div className="space-y-6">
              {/* Trust Overview Header */}
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 flex items-center">
                  <Shield className="h-6 w-6 text-yellow-600 mr-2" />
                  Trust Overview
                </h2>
                <div className="flex items-center space-x-4">
                  <Link to="/trust-accounts" className="text-yellow-600 hover:text-gray-800 text-sm font-medium">
                    Manage Trust Accounts →
                  </Link>
                  <Link to="/operating-accounts" className="text-blue-600 hover:text-gray-800 text-sm font-medium">
                    Operating Accounts →
                  </Link>
                </div>
              </div>

              {/* Trust Widgets Grid - 3 columns */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Column 1: Trust Summary + Accounts Needing Attention */}
                <div className="space-y-6">
                  <TrustSummaryWidget />
                  <AccountsAttentionWidget />
                </div>

                {/* Column 2: Trust Trends Chart */}
                <TrustTrendsWidget />

                {/* Column 3: Recent Activity + Case Allocations */}
                <div className="space-y-6">
                  <RecentTrustActivityWidget />
                  <CaseAllocationWidget />
                </div>
              </div>
            </div>
          )}

          {/* Key Metrics Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <MetricCard
              title="Firm Active Cases"
              value={metrics?.active_cases?.toString() || '0'}
              icon={CheckCircle}
              color="yellow"
              trend="Active matters"
              onClick={() => navigate('/cases')}
            />
            <MetricCard
              title="Firm Billable Hours (This Month)"
              value={metrics?.billable_hours_month?.toFixed(1) || '0.0'}
              icon={TrendingUp}
              color="blue"
              trend="Monthly total"
            />
            <ProjectionsWidget viewType="firm" userId={user.id} />
          </div>

          {/* Billing Analytics Row */}
          {(isVisible('revenue') || isVisible('arAging') || isVisible('unbilledWork')) && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {isVisible('revenue') && <RevenueWidget viewType="firm" />}
              {isVisible('arAging') && <ARAgingWidget viewType="firm" />}
              {isVisible('unbilledWork') && <UnbilledWorkWidget viewType="firm" />}
            </div>
          )}

          {/* Main Layout - Main content + Recent Activity Sidebar */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content Area - Left side, takes 2 columns on desktop */}
            <div className="lg:col-span-2 space-y-6">
              {/* Court Deadlines - CRITICAL - Full Width and Prominent */}
              {isVisible('courtDeadlines') && (
                <div>
                  <CourtDeadlinesWidget viewType="firm" userId={user.id} />
                </div>
              )}

              {/* Today's Schedule - Full Width */}
              {isVisible('todaysSchedule') && (
                <div>
                  <TodaysScheduleWidget userId={user.id} />
                </div>
              )}

              {/* Overdue Tasks - Full Width */}
              {isVisible('overdueTasks') && (
                <div>
                  <OverdueTasksWidget viewType="firm" userId={user.id} />
                </div>
              )}

              {/* Workload Widget - Full Width */}
              {isVisible('workload') && (
                <div>
                  <WorkloadWidget viewType="firm" userId={user.id} />
                </div>
              )}
            </div>

            {/* Recent Activity Sidebar - Right side on desktop, stacks below on mobile */}
            <div className="space-y-6">
              {/* Goals Widget */}
              {isVisible('goals') && <GoalsWidget viewType="firm" />}

              {isVisible('caseVolume') && <CaseVolumeWidget />}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Customize Panel — slide-over drawer for toggling widgets
const CustomizePanel = ({ widgetVisibility, onToggle, onClose }) => {
  // Group widgets by category
  const categories = {};
  for (const [key, widget] of Object.entries(WIDGET_REGISTRY)) {
    if (!categories[widget.category]) {
      categories[widget.category] = [];
    }
    categories[widget.category].push({ key, ...widget });
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Settings2 className="h-5 w-5 text-yellow-600 mr-2" />
          Customize Dashboard
        </h3>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-5">Choose which widgets appear on your dashboard. Your preferences are saved automatically.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {Object.entries(categories).map(([category, widgets]) => (
          <div key={category}>
            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">{category}</h4>
            <div className="space-y-2">
              {widgets.map(widget => {
                const visible = widgetVisibility[widget.key] !== false;
                return (
                  <button
                    key={widget.key}
                    onClick={() => onToggle(widget.key)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all duration-200 text-left ${
                      visible
                        ? 'border-yellow-200 bg-yellow-50 hover:bg-yellow-100'
                        : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <span className={`text-sm font-medium ${visible ? 'text-gray-900' : 'text-gray-500'}`}>
                      {widget.label}
                    </span>
                    {visible ? (
                      <Eye className="h-4 w-4 text-yellow-600" />
                    ) : (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Reusable metric card component
const MetricCard = ({ title, value, icon: Icon, color, trend, onClick }) => {
  const colorClasses = {
    red: {
      bg: 'bg-gradient-to-br from-red-50 to-red-100',
      icon: 'bg-gradient-to-br from-red-500 to-red-600 text-white',
      border: 'border-red-200',
      shadow: 'hover:shadow-red-100'
    },
    yellow: {
      bg: 'bg-gradient-to-br from-yellow-50 to-yellow-100',
      icon: 'bg-gradient-to-br from-yellow-500 to-yellow-600 text-gray-900',
      border: 'border-yellow-200',
      shadow: 'hover:shadow-yellow-100'
    },
    gray: {
      bg: 'bg-gradient-to-br from-gray-50 to-gray-100',
      icon: 'bg-gradient-to-br from-gray-500 to-gray-600 text-white',
      border: 'border-gray-200',
      shadow: 'hover:shadow-gray-100'
    },
    blue: {
      bg: 'bg-gradient-to-br from-primary-50 to-primary-100',
      icon: 'bg-gradient-to-br from-primary-500 to-primary-600 text-white',
      border: 'border-gray-200',
      shadow: 'hover:shadow-primary-100'
    }
  };

  const classes = colorClasses[color];

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
      className={`${classes.bg} p-6 rounded-xl shadow-lg border ${classes.border} transition-all duration-300 hover:shadow-2xl ${classes.shadow} hover:-translate-y-1 cursor-pointer group`}
    >
      <div className="flex items-center">
        <div className={`p-3 rounded-xl ${classes.icon} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
          <Icon className="h-6 w-6" />
        </div>
        <div className="ml-4 flex-1">
          <p className="text-sm font-semibold text-gray-700 mb-1">{title}</p>
          <p className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">{value}</p>
          {trend && (
            <p className="text-xs font-medium text-gray-600 mt-2 flex items-center">
              <span className="w-2 h-2 rounded-full bg-current mr-1.5 animate-pulse"></span>
              {trend}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
